<template>
  <MainHeader />
  <MainSidebar />
  <div class="main-content bg_gray d-flex flex-column transition overflow-hidden">
    <BreadcrumbMenu pageTitle="Compras" />

    <div class="flex-grow-1 overflow-auto px-3 pb-3">
      <div class="d-flex flex-wrap justify-content-end gap-2 mb-3">
        <router-link to="/importar-xml" class="btn btn-sm btn-outline-primary">
          Importar XML + PDF
        </router-link>
        <router-link to="/recepcion-mercaderia" class="btn btn-sm btn-primary">
          Recepción de mercadería
        </router-link>
      </div>

      <details class="legacy-guide-note mb-3">
        <summary>
          <span class="legacy-guide-note__icon"
            title="Esta función aparece en el sistema Windows, pero no encontramos registros que confirmen su uso actual."
            aria-label="Información sobre guías de proveedor">i</span>
          <span>Guías de ingreso de proveedor</span>
          <span class="legacy-guide-note__action">Ver detalle</span>
        </summary>
        <div class="legacy-guide-note__body">
          El chat con Rosa y la base actual confirman que Logística registra guías y facturas.
          Encontramos 9,266 guías de remisión y todas actualizan stock. El Windows evita duplicar
          la entrada modificando el mismo documento de guía cuando llega la factura: revierte el
          movimiento anterior y vuelve a aplicarlo dentro del guardado. Los campos “documento de
          referencia” son informativos y no controlan stock. La web replica ese flujo: registra
          la guía sin deuda y permite convertirla en factura sin duplicar el stock.
        </div>
      </details>

      <details class="function-coverage mb-3">
        <summary>
          <span class="legacy-guide-note__icon"
            title="Compara las funciones de logística del sistema Windows con lo disponible actualmente."
            aria-label="Información de cobertura funcional">i</span>
          <span>Funciones de logística y cobertura actual</span>
          <span class="legacy-guide-note__action">Consultar</span>
        </summary>
        <div class="function-coverage__body">
          <p class="function-coverage__intro">
            Este inventario distingue las funciones terminadas de las que todavía requieren
            validación o desarrollo. Una función marcada como parcial no debe considerarse lista.
          </p>
          <div class="table-responsive">
            <table class="table table-sm align-middle mb-0 function-coverage__table">
              <thead>
                <tr><th>Función</th><th>Qué hace</th><th>Estado web</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td>Factura o boleta de proveedor</td>
                  <td>Registra productos, costos, impuestos, proveedor y entrada inmediata al stock.</td>
                  <td><span class="coverage-status is-ready">Disponible</span></td>
                </tr>
                <tr>
                  <td>Importación XML + PDF</td>
                  <td>Lee la factura SUNAT y distribuye automáticamente productos y tallas para revisión.</td>
                  <td><span class="coverage-status is-ready">Disponible</span></td>
                </tr>
                <tr>
                  <td>Recepción física</td>
                  <td>Confirma cantidades parciales, barcodes y precios antes de ingresar mercadería al stock.</td>
                  <td><span class="coverage-status is-ready">Disponible</span></td>
                </tr>
                <tr>
                  <td>Guía de ingreso de proveedor</td>
                  <td>Registra la mercadería con la guía cuando la factura todavía no ha llegado.</td>
                  <td><span class="coverage-status is-ready">Disponible</span></td>
                </tr>
                <tr>
                  <td>Vincular guía con factura</td>
                  <td>Convierte la guía existente en factura, conservando un solo movimiento de stock.</td>
                  <td><span class="coverage-status is-ready">Disponible</span></td>
                </tr>
                <tr>
                  <td>Guía electrónica SUNAT</td>
                  <td>Emite y envía la guía de remisión a SUNAT, mostrando si fue aceptada o rechazada.</td>
                  <td><span class="coverage-status is-pending">Pendiente</span></td>
                </tr>
                <tr>
                  <td>Nota de crédito de compra</td>
                  <td>Registra devolución o descuento del proveedor y ajusta stock o cuenta por pagar.</td>
                  <td><span class="coverage-status is-ready">Disponible</span></td>
                </tr>
                <tr>
                  <td>Ajuste de inventario</td>
                  <td>Suma o resta unidades por diferencias, daños, pérdidas o correcciones autorizadas.</td>
                  <td><span class="coverage-status is-ready">Disponible</span></td>
                </tr>
                <tr>
                  <td>Transferencia entre almacenes</td>
                  <td>Crea, emite, recibe o anula una guía entre sedes y mueve el stock correspondiente.</td>
                  <td><span class="coverage-status is-ready">Disponible</span></td>
                </tr>
                <tr>
                  <td>Productos seriados</td>
                  <td>Guarda un número de serie individual por unidad recibida para su seguimiento.</td>
                  <td><span class="coverage-status is-review">Sin evidencia de uso</span></td>
                </tr>
                <tr>
                  <td>Editar una compra</td>
                  <td>Corrige una compra ya registrada recalculando stock, kardex y cuenta por pagar.</td>
                  <td><span class="coverage-status is-ready">Disponible</span></td>
                </tr>
                <tr>
                  <td>Guía interna desde compra</td>
                  <td>Prepara el traslado a otra sede usando las cantidades indicadas en “Otra sede”.</td>
                  <td><span class="coverage-status is-ready">Disponible</span></td>
                </tr>
                <tr>
                  <td>Imprimir documento</td>
                  <td>Genera una representación imprimible de la factura, guía o movimiento registrado.</td>
                  <td><span class="coverage-status is-partial">Parcial</span></td>
                </tr>
                <tr>
                  <td>Historial y anulación de compras</td>
                  <td>Consulta documentos y revierte una compra junto con el stock y la cuenta asociada.</td>
                  <td><span class="coverage-status is-ready">Disponible</span></td>
                </tr>
                <tr>
                  <td>Stock y kardex por almacén</td>
                  <td>Muestra existencias y movimientos históricos por producto, talla y sede.</td>
                  <td><span class="coverage-status is-ready">Disponible</span></td>
                </tr>
                <tr>
                  <td>Inventario físico</td>
                  <td>Compara el conteo real con el sistema y genera ajustes por las diferencias.</td>
                  <td><span class="coverage-status is-ready">Disponible</span></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="function-coverage__legend">
            <strong>Disponible:</strong> interfaz y backend presentes.
            <strong>Parcial:</strong> existe una parte técnica, pero falta completar o validar el flujo visible.
            <strong>Sin evidencia de uso:</strong> el Windows lo soporta, pero las bases revisadas no contienen registros.
            <strong>Pendiente:</strong> no está implementado.
          </div>
        </div>
      </details>

      <!-- Filtros -->
      <div class="card mb-3">
        <div class="card-body py-3">
          <div class="row g-2 align-items-end">
            <div class="col-12 col-md-3">
              <label class="form-label mb-1 small fw-semibold">Proveedor</label>
              <input v-model="filtros.q" @input="buscarDebounced" type="text"
                class="form-control form-control-sm" placeholder="Nombre o nro. documento…" />
            </div>
            <div class="col-6 col-md-2">
              <label class="form-label mb-1 small fw-semibold">Desde</label>
              <input v-model="filtros.f1" @change="cargar" type="date" class="form-control form-control-sm" />
            </div>
            <div class="col-6 col-md-2">
              <label class="form-label mb-1 small fw-semibold">Hasta</label>
              <input v-model="filtros.f2" @change="cargar" type="date" class="form-control form-control-sm" />
            </div>
            <div class="col-6 col-md-2">
              <label class="form-label mb-1 small fw-semibold">Documento</label>
              <select v-model="filtros.idtipodoc" @change="cargar" class="form-select form-select-sm">
                <option value="">Todos</option>
                <option value="2">Facturas</option>
                <option value="1">Boletas</option>
                <option value="3">Guías</option>
              </select>
            </div>
            <div class="col-6 col-md-2">
              <label class="form-label mb-1 small fw-semibold">Estado</label>
              <select v-model="filtros.estado" @change="cargar" class="form-select form-select-sm">
                <option value="">Todos</option>
                <option value="true">Activas</option>
                <option value="false">Anuladas</option>
              </select>
            </div>
            <div class="col-6 col-md-2">
              <label class="form-label mb-1 small fw-semibold">Almacén</label>
              <select v-model="filtros.idalmacen" @change="cargar" class="form-select form-select-sm">
                <option value="">Ambos</option>
                <option value="1">939</option>
                <option value="15">1179</option>
              </select>
            </div>
            <div class="col-12 col-md-1">
              <button @click="limpiar" class="btn btn-sm btn-outline-secondary w-100">✕</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabla -->
      <div class="card">
        <div class="card-body p-0">
          <div v-if="cargando" class="text-center py-5 text-muted">
            <div class="spinner-border spinner-border-sm me-2"></div>Cargando…
          </div>
          <div v-else-if="!compras.length" class="text-center py-5 text-muted">
            No se encontraron compras con los filtros aplicados.
          </div>
          <div v-else class="table-responsive">
            <table class="table table-hover table-sm mb-0">
              <thead class="table-light">
                <tr>
                  <th class="ps-3">Documento</th>
                  <th>Proveedor</th>
                  <th>Almacén</th>
                  <th>Fecha emisión</th>
                  <th>Fecha recepción</th>
                  <th class="text-end">Importe</th>
                  <th class="text-center">Estado</th>
                  <th class="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="c in compras" :key="c.idreg" :class="!c.estado ? 'text-muted' : ''">
                  <td class="ps-3">
                    <span class="badge bg-light text-dark border font-monospace">
                      {{ c.tipo_documento_abrev || 'DOC' }} {{ c.serie }}-{{ c.numero }}
                    </span>
                  </td>
                  <td>
                    <div class="fw-semibold small">{{ c.proveedor || '—' }}</div>
                  </td>
                  <td class="small text-muted">{{ c.almacen }}</td>
                  <td class="small">{{ formatFecha(c.fecha_emision) }}</td>
                  <td class="small">{{ formatFecha(c.fecha_recep) }}</td>
                  <td class="text-end fw-semibold small"
                      :class="!c.estado ? 'text-decoration-line-through text-muted' : ''">
                    {{ c.moneda === 'USD' ? '$' : 'S/' }} {{ formatNum(c.montototdoc) }}
                  </td>
                  <td class="text-center">
                    <span :class="c.estado ? 'badge bg-success-subtle text-success' : 'badge bg-danger-subtle text-danger'">
                      {{ c.estado ? 'Activa' : 'Anulada' }}
                    </span>
                  </td>
                  <td class="text-center">
                    <div class="d-flex gap-1 justify-content-center">
                      <button @click="verDetalle(c)" class="btn btn-xs btn-outline-primary" title="Ver detalle">
                        <!-- eye icon -->
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      </button>
                      <button v-if="c.estado" @click="editarCompra(c)"
                        class="btn btn-xs btn-outline-secondary" title="Editar compra">
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z"/></svg>
                      </button>
                      <button v-if="c.estado" @click="pedirAnular(c)"
                        class="btn btn-xs btn-outline-danger" title="Anular compra">
                        <!-- trash icon -->
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                      </button>
                      <button v-if="c.estado && Number(c.idtipodoc) === 3" @click="abrirConversion(c)"
                        class="btn btn-xs btn-outline-success" title="Convertir guía en factura">
                        FAC
                      </button>
                      <button v-if="c.estado && [1,2].includes(Number(c.idtipodoc))" @click="abrirNotaCredito(c)"
                        class="btn btn-xs btn-outline-warning" title="Registrar nota de crédito">
                        NC
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="totalPags > 1" class="d-flex justify-content-between align-items-center px-3 py-2 border-top">
            <small class="text-muted">{{ total }} registros</small>
            <div class="d-flex gap-1">
              <button class="btn btn-sm btn-outline-secondary" :disabled="pagina <= 1" @click="paginar(pagina-1)">‹</button>
              <span class="btn btn-sm btn-secondary disabled">{{ pagina }} / {{ totalPags }}</span>
              <button class="btn btn-sm btn-outline-secondary" :disabled="pagina >= totalPags" @click="paginar(pagina+1)">›</button>
            </div>
          </div>
        </div>
      </div>

    </div><!-- /flex-grow-1 -->

    <MainFooter />
  </div>

  <!-- Modal detalle -->
  <div v-if="detalleModal" class="modal fade show d-block" style="background:rgba(0,0,0,.45);z-index:1055" @click.self="detalleModal=null">
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header py-2">
          <h6 class="modal-title fw-bold">
            {{ detalleModal.tipo_documento_abrev }} {{ detalleModal.serie }}-{{ detalleModal.numero }}
          </h6>
          <button class="btn-close btn-sm" @click="detalleModal=null"></button>
        </div>
        <div class="modal-body py-2">
          <div v-if="cargandoDetalle" class="text-center py-3">
            <div class="spinner-border spinner-border-sm"></div>
          </div>
          <div v-else>
            <div class="row g-2 mb-3">
              <div class="col-12 col-md-3">
                <div class="small text-muted">Proveedor</div>
                <div class="fw-semibold">{{ detalleModal.proveedor || '—' }}</div>
              </div>
              <div class="col-6 col-md-3">
                <div class="small text-muted">Almacén</div>
                <div>{{ detalleModal.almacen }}</div>
              </div>
              <div class="col-6 col-md-2">
                <div class="small text-muted">Fecha de emisión</div>
                <div>{{ formatFecha(detalleModal.fecha_emision) }}</div>
              </div>
              <div class="col-6 col-md-2">
                <div class="small text-muted">Fecha de recepción</div>
                <div>{{ formatFecha(detalleModal.fecha_recep) }}</div>
              </div>
              <div class="col-6 col-md-2">
                <div class="small text-muted">Total</div>
                <div class="fw-bold text-primary">S/ {{ formatNum(detalleModal.montototdoc) }}</div>
              </div>
            </div>
            <div v-if="Number(detalleModal.ajuste || 0)" class="small text-muted mb-2">
              Ajuste aplicado: <strong>S/ {{ formatNum(detalleModal.ajuste) }}</strong>
            </div>
            <div v-if="detalleModal.detalles?.length" class="d-flex gap-3 mb-2 small text-muted">
              <span>{{ detalleGrupos.length }} productos</span>
              <span>{{ detalleModal.detalles.reduce((s,d) => s + parseFloat(d.cantidad||0), 0).toFixed(0) }} unidades en total</span>
            </div>
            <div class="table-responsive" style="max-height:420px;overflow-y:auto">
              <table class="table table-sm mb-0">
                <thead class="table-light sticky-top">
                  <tr>
                    <th style="width:110px">Cód. Prov.</th>
                    <th>Producto</th>
                    <th class="text-center" style="width:80px">Cant.</th>
                    <th class="text-center" style="width:90px">Otra sede</th>
                    <th class="text-end" style="width:90px">Precio</th>
                    <th class="text-end" style="width:100px">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="g in detalleGrupos" :key="g.key">
                    <tr class="table-light" style="cursor:pointer" @click="toggleGrupo(g.key)">
                      <td class="font-monospace small text-muted">{{ g.codigo_proveedor || '—' }}</td>
                      <td class="small fw-semibold">
                        <span class="me-1" style="display:inline-block;transition:transform .15s" :style="gruposAbiertos[g.key] ? 'transform:rotate(90deg)' : ''">›</span>
                        {{ g.producto }}
                        <span class="badge bg-secondary-subtle text-secondary ms-1">{{ g.lineas.length }} tallas</span>
                      </td>
                      <td class="text-center fw-bold">{{ g.cantTotal.toFixed(0) }}</td>
                      <td class="text-center fw-bold text-warning-emphasis">{{ g.otraSedeTotal.toFixed(0) }}</td>
                      <td class="text-end small text-muted">S/ {{ formatNum(g.lineas[0].precio) }}</td>
                      <td class="text-end fw-semibold">S/ {{ formatNum(g.netoTotal) }}</td>
                    </tr>
                    <template v-if="gruposAbiertos[g.key]">
                      <tr v-for="d in g.lineas" :key="d.iddet" class="bg-white">
                        <td class="small text-muted ps-3" style="font-size:.75rem">{{ d.codbarras }}</td>
                        <td class="small ps-3">
                          <span class="badge bg-primary-subtle text-primary me-1" v-if="d.talla_usa">US {{ d.talla_usa }}</span>
                          <span class="badge bg-secondary-subtle text-secondary" v-if="d.talla_peru">PE {{ d.talla_peru }}</span>
                          <span v-if="!d.talla_usa && !d.talla_peru" class="text-muted">—</span>
                        </td>
                        <td class="text-center small">{{ parseFloat(d.cantidad).toFixed(0) }}</td>
                        <td class="text-center small">{{ parseFloat(d.cantp1 || 0).toFixed(0) }}</td>
                        <td class="text-end small">S/ {{ formatNum(d.precio) }}</td>
                        <td class="text-end small">S/ {{ formatNum(d.neto) }}</td>
                      </tr>
                    </template>
                  </template>
                  <tr v-if="!detalleModal.detalles?.length">
                    <td colspan="6" class="text-center text-muted py-3">Sin detalle disponible</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div v-if="!cargandoDetalle && detalleModal.estado" class="modal-footer py-2">
          <button class="btn btn-sm btn-outline-secondary" @click="editarCompra(detalleModal)">Editar compra</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal confirmación anular -->
  <div v-if="anularModal" class="modal fade show d-block" style="background:rgba(0,0,0,.55);z-index:1060" @click.self="anularModal=null">
    <div class="modal-dialog modal-dialog-centered" style="max-width:420px">
      <div class="modal-content border-danger">
        <div class="modal-header py-2 bg-danger-subtle">
          <h6 class="modal-title fw-bold text-danger">Anular compra</h6>
          <button class="btn-close btn-sm" @click="anularModal=null"></button>
        </div>
        <div class="modal-body">
          <p class="mb-2">¿Seguro que quieres anular esta compra?</p>
          <div class="rounded-3 bg-light border p-2 small mb-3">
            <div class="fw-semibold">{{ anularModal.tipo_documento_abrev }} {{ anularModal.serie }}-{{ anularModal.numero }}</div>
            <div class="text-muted">{{ anularModal.proveedor }}</div>
            <div class="text-muted">S/ {{ formatNum(anularModal.montototdoc) }} — {{ formatFecha(anularModal.fecha_emision) }}</div>
          </div>
          <div class="alert alert-warning py-2 small mb-0">
            El stock registrado de esta compra se revertirá automáticamente.
            La cuenta por pagar asociada también quedará anulada.
            <strong>Esta acción no se puede deshacer.</strong>
          </div>
          <div v-if="errorAnular" class="alert alert-danger mt-2 py-2 small mb-0">{{ errorAnular }}</div>
        </div>
        <div class="modal-footer py-2">
          <button class="btn btn-sm btn-outline-secondary" @click="anularModal=null" :disabled="anulando">Cancelar</button>
          <button class="btn btn-sm btn-danger" @click="confirmarAnular" :disabled="anulando">
            <span v-if="anulando" class="spinner-border spinner-border-sm me-1"></span>
            {{ anulando ? 'Anulando…' : 'Sí, anular' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-if="conversionModal" class="modal fade show d-block" style="background:rgba(0,0,0,.55);z-index:1060" @click.self="conversionModal=null">
    <div class="modal-dialog modal-dialog-centered" style="max-width:520px">
      <div class="modal-content">
        <div class="modal-header py-2">
          <h6 class="modal-title fw-bold">Convertir guía en factura</h6>
          <button class="btn-close btn-sm" @click="conversionModal=null"></button>
        </div>
        <div class="modal-body">
          <div class="alert alert-info py-2 small">Se conservará el mismo registro. El stock se recalcula dentro de una sola transacción y no se duplica.</div>
          <div class="row g-2">
            <div class="col-4"><label class="form-label small">Serie factura</label><input v-model="factura.serie" class="form-control form-control-sm" maxlength="10"></div>
            <div class="col-8"><label class="form-label small">Número factura</label><input v-model="factura.numero" class="form-control form-control-sm" maxlength="20"></div>
            <div class="col-6"><label class="form-label small">Fecha emisión</label><input v-model="factura.fecha_emision" type="date" class="form-control form-control-sm"></div>
            <div class="col-6"><label class="form-label small">Días de crédito</label><input v-model.number="factura.ndiasvenc" type="number" min="0" class="form-control form-control-sm"></div>
          </div>
          <div v-if="errorConversion" class="alert alert-danger mt-2 py-2 small mb-0">{{ errorConversion }}</div>
        </div>
        <div class="modal-footer py-2">
          <button class="btn btn-sm btn-outline-secondary" @click="conversionModal=null" :disabled="convirtiendo">Cancelar</button>
          <button class="btn btn-sm btn-success" @click="confirmarConversion" :disabled="convirtiendo || !factura.serie || !factura.numero">
            <span v-if="convirtiendo" class="spinner-border spinner-border-sm me-1"></span>
            {{ convirtiendo ? 'Convirtiendo…' : 'Convertir en factura' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-if="notaCreditoModal" class="modal fade show d-block" style="background:rgba(0,0,0,.55);z-index:1060" @click.self="notaCreditoModal=null">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header py-2">
          <h6 class="modal-title fw-bold">Nota de crédito de {{ notaCreditoModal.serie }}-{{ notaCreditoModal.numero }}</h6>
          <button class="btn-close btn-sm" @click="notaCreditoModal=null"></button>
        </div>
        <div class="modal-body">
          <div class="row g-2 mb-3">
            <div class="col-3"><label class="form-label small">Serie NC</label><input v-model="notaCredito.serie" class="form-control form-control-sm" maxlength="10"></div>
            <div class="col-4"><label class="form-label small">Número NC</label><input v-model="notaCredito.numero" class="form-control form-control-sm" maxlength="20"></div>
            <div class="col-5"><label class="form-label small">Fecha</label><input v-model="notaCredito.fecha_emision" type="date" class="form-control form-control-sm"></div>
            <div class="col-12">
              <label class="form-label small">Motivo</label>
              <input v-model="notaCredito.observacion" class="form-control form-control-sm" maxlength="500" placeholder="Devolución, descuento o corrección del proveedor">
            </div>
            <div class="col-12 form-check ms-2">
              <input id="ncStock" v-model="notaCredito.actualizastock" type="checkbox" class="form-check-input">
              <label for="ncStock" class="form-check-label small">Hay devolución física: descontar estas unidades del stock</label>
            </div>
          </div>
          <div class="table-responsive" style="max-height:300px">
            <table class="table table-sm align-middle">
              <thead class="table-light"><tr><th>Producto / talla</th><th style="width:110px">Cantidad NC</th><th class="text-end">Precio</th></tr></thead>
              <tbody>
                <tr v-for="item in notaCredito.items" :key="item.iddet">
                  <td class="small">{{ item.producto }} <span v-if="item.talla_usa" class="text-muted">US {{ item.talla_usa }}</span></td>
                  <td><input v-model.number="item.cantidad_nc" type="number" min="0" :max="Number(item.cantidad)" step="1" class="form-control form-control-sm"></td>
                  <td class="text-end small">S/ {{ formatNum(item.precio) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="d-flex justify-content-end fw-bold">Total NC: S/ {{ formatNum(totalNotaCredito) }}</div>
          <div v-if="errorNotaCredito" class="alert alert-danger mt-2 py-2 small mb-0">{{ errorNotaCredito }}</div>
        </div>
        <div class="modal-footer py-2">
          <button class="btn btn-sm btn-outline-secondary" @click="notaCreditoModal=null" :disabled="guardandoNotaCredito">Cancelar</button>
          <button class="btn btn-sm btn-warning" @click="guardarNotaCredito" :disabled="guardandoNotaCredito || !notaCredito.serie || !notaCredito.numero || totalNotaCredito <= 0">
            <span v-if="guardandoNotaCredito" class="spinner-border spinner-border-sm me-1"></span>
            Registrar nota de crédito
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MainHeader    from '../../components/Layouts/MainHeader.vue';
import MainSidebar   from '../../components/Layouts/MainSidebar.vue';
import BreadcrumbMenu from '../../components/Common/BreadcrumbMenu.vue';
import MainFooter    from '../../components/Layouts/MainFooter.vue';
import api from '../../services/api';

export default {
  name: 'ComprasPage',
  components: { MainHeader, MainSidebar, BreadcrumbMenu, MainFooter },
  data() {
    return {
      compras: [],
      total: 0,
      pagina: 1,
      pageSize: 30,
      cargando: false,
      detalleModal: null,
      cargandoDetalle: false,
      filtros: { q: '', f1: '', f2: '', estado: 'true', idalmacen: '', idtipodoc: '' },
      debounceTimer: null,
      gruposAbiertos: {},
      anularModal: null,
      anulando: false,
      errorAnular: null,
      conversionModal: null,
      convirtiendo: false,
      errorConversion: null,
      factura: { serie: '', numero: '', fecha_emision: '', ndiasvenc: 0 },
      notaCreditoModal: null,
      guardandoNotaCredito: false,
      errorNotaCredito: null,
      notaCredito: { serie: '', numero: '', fecha_emision: '', observacion: '', actualizastock: true, items: [] },
    };
  },
  computed: {
    totalPags() { return Math.ceil(this.total / this.pageSize); },
    detalleGrupos() {
      if (!this.detalleModal?.detalles?.length) return [];
      const map = new Map();
      for (const d of this.detalleModal.detalles) {
        const key = `${d.codigo_proveedor||''}||${d.producto}`;
        if (!map.has(key)) {
          map.set(key, { key, codigo_proveedor: d.codigo_proveedor, producto: d.producto, lineas: [], cantTotal: 0, otraSedeTotal: 0, netoTotal: 0 });
        }
        const g = map.get(key);
        g.lineas.push(d);
        g.cantTotal += parseFloat(d.cantidad || 0);
        g.otraSedeTotal += parseFloat(d.cantp1 || 0);
        g.netoTotal += parseFloat(d.neto || 0);
      }
      return Array.from(map.values());
    },
    totalNotaCredito() {
      return this.notaCredito.items.reduce((s, item) =>
        s + Number(item.cantidad_nc || 0) * Number(item.precio || 0), 0);
    },
  },
  mounted() { this.cargar(); },
  methods: {
    buscarDebounced() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => { this.pagina = 1; this.cargar(); }, 400);
    },
    async cargar() {
      this.cargando = true;
      try {
        const params = { page: this.pagina, limit: this.pageSize };
        if (this.filtros.q)         params.q         = this.filtros.q;
        if (this.filtros.f1)        params.f1        = this.filtros.f1;
        if (this.filtros.f2)        params.f2        = this.filtros.f2;
        if (this.filtros.estado)    params.estado    = this.filtros.estado;
        if (this.filtros.idalmacen) params.idalmacen = this.filtros.idalmacen;
        if (this.filtros.idtipodoc) params.idtipodoc = this.filtros.idtipodoc;
        const r = await api.get('/compras', { params });
        const d = r.data?.data ?? r.data;
        this.compras = Array.isArray(d) ? d : (d?.data ?? []);
        this.total   = r.data?.pagination?.total ?? d?.total ?? this.compras.length;
      } catch(e) {
        console.error(e);
        this.compras = [];
      } finally { this.cargando = false; }
    },
    toggleGrupo(key) {
      this.gruposAbiertos = { ...this.gruposAbiertos, [key]: !this.gruposAbiertos[key] };
    },
    editarCompra(compra) {
      this.$router.push(`/compras/${compra.idreg}/editar`);
    },
    async verDetalle(compra) {
      this.detalleModal = { ...compra, detalles: [] };
      this.gruposAbiertos = {};
      this.cargandoDetalle = true;
      try {
        const r = await api.get(`/compras/${compra.idreg}`);
        this.detalleModal = r.data?.data ?? r.data;
      } catch(e) { console.error(e); }
      finally { this.cargandoDetalle = false; }
    },
    pedirAnular(compra) {
      this.anularModal = compra;
      this.errorAnular = null;
    },
    async abrirConversion(compra) {
      this.errorConversion = null;
      this.factura = { serie: '', numero: '', fecha_emision: new Date().toISOString().slice(0, 10), ndiasvenc: 0 };
      this.conversionModal = { ...compra, detalles: [] };
      try {
        const r = await api.get(`/compras/${compra.idreg}`);
        this.conversionModal = r.data?.data ?? r.data;
      } catch (e) {
        this.errorConversion = e.response?.data?.error || e.message;
      }
    },
    async confirmarConversion() {
      if (!this.conversionModal?.detalles?.length) return;
      this.convirtiendo = true;
      this.errorConversion = null;
      const c = this.conversionModal;
      try {
        await api.post(`/compras/guias/${c.idreg}/convertir-factura`, {
          ...this.factura,
          fecha_recep: this.factura.fecha_emision,
          moneda: c.moneda || 'PEN', tipo_camb: Number(c.tipo_camb) || 1,
          igv: Number(c.igv) || 18, con_igv: c.con_igv !== false,
          valor_igv: Number(c.valor_igv) || 0, montototdoc: Number(c.montototdoc) || 0,
          observacion: c.observacion || '',
          items: c.detalles.map(d => ({
            idpres: Number(d.idpres), cantidad: Number(d.cantidad), precio: Number(d.precio),
            afecto: d.afecto !== false, porcdescuento: Number(d.porcdescuento) || 0,
            lote: String(d.lote || ''), tipoprod: Number(d.tipoprod) || 1,
          })),
        });
        this.conversionModal = null;
        await this.cargar();
      } catch (e) {
        const d = e.response?.data;
        this.errorConversion = d?.details?.map(x => x.msg).join(' · ') || d?.error || e.message;
      } finally { this.convirtiendo = false; }
    },
    async abrirNotaCredito(compra) {
      this.errorNotaCredito = null;
      this.notaCreditoModal = { ...compra, detalles: [] };
      try {
        const r = await api.get(`/compras/${compra.idreg}`);
        const c = r.data?.data ?? r.data;
        this.notaCreditoModal = c;
        this.notaCredito = {
          serie: '', numero: '', fecha_emision: new Date().toISOString().slice(0, 10),
          observacion: '', actualizastock: true,
          items: (c.detalles || []).map(item => ({ ...item, cantidad_nc: Number(item.cantidad) })),
        };
      } catch (e) { this.errorNotaCredito = e.response?.data?.error || e.message; }
    },
    async guardarNotaCredito() {
      const c = this.notaCreditoModal;
      const items = this.notaCredito.items.filter(item => Number(item.cantidad_nc) > 0);
      if (!c || !items.length) return;
      this.guardandoNotaCredito = true;
      this.errorNotaCredito = null;
      try {
        const factor = c.con_igv === false ? 1 + (Number(c.igv) || 18) / 100 : 1;
        const total = this.totalNotaCredito;
        const valorIgv = c.con_igv === false ? total - total / factor : Number(c.valor_igv || 0) * (total / Number(c.montototdoc || 1));
        await api.post('/compras/notas-credito', {
          idalmacen: Number(c.idalmacen), idorigen: Number(c.idorigen),
          serie: this.notaCredito.serie.trim(), numero: this.notaCredito.numero.trim(),
          fecha_emision: this.notaCredito.fecha_emision,
          montototdoc: Number(total.toFixed(2)), valor_igv: Number(valorIgv.toFixed(2)),
          igv: Number(c.igv) || 18, con_igv: c.con_igv !== false,
          moneda: c.moneda || 'PEN', tipo_camb: Number(c.tipo_camb) || 1,
          observacion: this.notaCredito.observacion,
          actualizastock: this.notaCredito.actualizastock,
          idreg_original: Number(c.idreg), serie_original: c.serie, numero_original: c.numero,
          items: items.map(item => ({
            idpres: Number(item.idpres), cantidad: Number(item.cantidad_nc), precio: Number(item.precio),
            afecto: item.afecto !== false, porcdescuento: Number(item.porcdescuento) || 0,
            lote: String(item.lote || ''), tipoprod: Number(item.tipoprod) || 1,
          })),
        });
        this.notaCreditoModal = null;
        await this.cargar();
      } catch (e) {
        const d = e.response?.data;
        this.errorNotaCredito = d?.details?.map(x => x.msg).join(' · ') || d?.error || e.message;
      } finally { this.guardandoNotaCredito = false; }
    },
    async confirmarAnular() {
      if (!this.anularModal) return;
      this.anulando = true;
      this.errorAnular = null;
      try {
        await api.post(`/compras/${this.anularModal.idreg}/anular`);
        this.anularModal = null;
        this.cargar();
      } catch(e) {
        this.errorAnular = e.response?.data?.error || e.message;
      } finally {
        this.anulando = false;
      }
    },
    paginar(p) { this.pagina = p; this.cargar(); },
    limpiar() {
      this.filtros = { q: '', f1: '', f2: '', estado: 'true', idalmacen: '', idtipodoc: '' };
      this.pagina = 1;
      this.cargar();
    },
    formatFecha(f) {
      if (!f) return '—';
      const s = String(f).substring(0, 10);
      return new Date(s + 'T00:00:00').toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    formatNum(n) {
      return parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.legacy-guide-note {
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  background: #eff6ff;
  color: #1e3a5f;
}
.legacy-guide-note summary {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 42px;
  padding: 8px 12px;
  cursor: pointer;
  list-style: none;
  font-size: 13px;
  font-weight: 600;
}
.legacy-guide-note summary::-webkit-details-marker { display: none; }
.legacy-guide-note__icon {
  display: inline-grid;
  place-items: center;
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  border: 1px solid #2563eb;
  border-radius: 50%;
  color: #2563eb;
  font: 700 12px/1 Georgia, serif;
}
.legacy-guide-note__action {
  margin-left: auto;
  color: #2563eb;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}
.legacy-guide-note__body {
  padding: 0 12px 12px 42px;
  font-size: 12px;
  line-height: 1.55;
}
.function-coverage {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #263238;
}
.function-coverage summary {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 42px;
  padding: 8px 12px;
  cursor: pointer;
  list-style: none;
  font-size: 13px;
  font-weight: 600;
}
.function-coverage summary::-webkit-details-marker { display: none; }
.function-coverage__body { border-top: 1px solid #e5e7eb; padding: 12px; }
.function-coverage__intro { margin-bottom: 10px; color: #64748b; font-size: 12px; }
.function-coverage__table { min-width: 760px; font-size: 12px; }
.function-coverage__table th { background: #f8fafc; color: #475569; font-size: 11px; }
.function-coverage__table th:first-child { width: 24%; }
.function-coverage__table th:last-child { width: 130px; }
.coverage-status {
  display: inline-block;
  min-width: 88px;
  padding: 3px 7px;
  border: 1px solid transparent;
  border-radius: 4px;
  text-align: center;
  font-size: 10px;
  font-weight: 700;
}
.coverage-status.is-ready { border-color: #86efac; background: #f0fdf4; color: #166534; }
.coverage-status.is-partial { border-color: #fde68a; background: #fffbeb; color: #92400e; }
.coverage-status.is-review { border-color: #93c5fd; background: #eff6ff; color: #1d4ed8; }
.coverage-status.is-pending { border-color: #fecaca; background: #fef2f2; color: #991b1b; }
.function-coverage__legend {
  margin-top: 10px;
  padding: 9px 10px;
  border-left: 3px solid #64748b;
  background: #f8fafc;
  color: #475569;
  font-size: 11px;
  line-height: 1.6;
}
@media (max-width: 575px) {
  .legacy-guide-note summary, .function-coverage summary { align-items: flex-start; }
  .legacy-guide-note__action { display: none; }
  .legacy-guide-note__body { padding-left: 12px; }
}
</style>
